<template>
  <v-data-table
    :headers="headers"
    :items="pricing"
    hide-default-footer
    :items-per-page="-1"
    :no-data-text="$t('pricingNoPrice')"
    dense
  >
    <template v-slot:item.prepayment="{ item }">
      <span>{{ item.prepayment | prepaymentFilter }}</span>
    </template>
    <template v-slot:top>
      <v-card-title>
        {{ $t("pricingTitle") }}
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" persistent max-width="500px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon>
              <v-icon>mdi-plus-circle</v-icon>
            </v-btn>
          </template>
          <v-card tile>
            <v-card-title>
              <span class="headline">{{ pricesDialogTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="selectedPrice"
                :items="items"
                :label="pricesDialogSelectLabel"
                v-on:change="selectionChanged"
              ></v-select>
              <v-text-field
                v-if="
                  selectedPrice === items[0] || editedItem.session_price > 0
                "
                type="number"
                v-model="editedItem.session_price"
                :label="$t('pricingSession')"
                dense
                class="mt-4"
                required
                :error-messages="priceSessionErrors"
                @input="$v.editedItem.session_price.$touch()"
                @blur="$v.editedItem.session_price.$touch()"
              ></v-text-field>
              <v-text-field
                v-if="
                  selectedPrice === items[1] || editedItem.personal_price > 0
                "
                type="number"
                v-model="editedItem.personal_price"
                :label="$t('pricingPersonal')"
                dense
                class="mt-4"
                required
                :error-messages="pricePersonalErrors"
                @input="$v.editedItem.personal_price.$touch()"
                @blur="$v.editedItem.personal_price.$touch()"
              ></v-text-field>
              <prepayment-input
                v-model="editedItem.prepayment"
                :error-messages="pricePrepaymentErrors"
                @input="$v.editedItem.prepayment.$touch()"
                @blur="$v.editedItem.prepayment.$touch()"
              ></prepayment-input>
              <v-text-field
                type="time"
                v-model="editedItem.start"
                :label="$t('pricingBegin')"
                dense
                class="mt-4"
                required
                :error-messages="priceStartErrors"
                @input="$v.editedItem.start.$touch()"
                @blur="$v.editedItem.start.$touch()"
              ></v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" text @click="close">{{
                $t("btnCancel")
              }}</v-btn>
              <v-btn
                color="green darken-1"
                text
                @click="save"
                :disabled="$v.$invalid"
                >{{ $t("btnSave") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card tile>
            <v-card-title class="headline">{{
              $t("pricingDelete")
            }}</v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" text @click="closeDelete">{{
                $t("btnNo")
              }}</v-btn>
              <v-btn color="green darken-1" text @click="deleteItemConfirm">{{
                $t("btnYes")
              }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-title>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
      <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  minValue,
  maxValue,
  requiredIf,
  helpers,
} from "vuelidate/lib/validators";
import PrepaymentInput from "@/components/PrepaymentInput.vue";

const priceRegexp = helpers.regex("price", /^[0-9,.]{1,20}$/);

export default {
  name: "PricingTable",

  props: ["pricing"],

  mixins: [validationMixin],

  data() {
    return {
      datepicker: false,
      dialog: false,
      dialogDelete: false,
      headers: [
        {
          text: this.$i18n.t("pricingSession"),
          align: "start",
          sortable: false,
          value: "session_price",
        },
        {
          text: this.$i18n.t("pricingPersonal"),
          align: "start",
          sortable: false,
          value: "personal_price",
        },
        {
          text: this.$i18n.t("pricingPrepayment"),
          align: "start",
          sortable: false,
          value: "prepayment",
        },
        {
          text: this.$i18n.t("pricingBegin"),
          align: "start",
          sortable: false,
          value: "start",
        },
        {
          text: this.$i18n.t("pricingManagement"),
          align: "end",
          sortable: false,
          value: "actions",
        },
      ],
      editedIndex: -1,
      editedItem: {
        session_price: "",
        personal_price: null,
        prepayment: 0,
        start: "",
        validate: false,
      },
      editedCurrentItem: {},
      defaultItem: {
        session_price: "",
        personal_price: null,
        prepayment: 0,
        start: "",
        validate: false,
      },
      items: [this.$i18n.t("pricingSession"), this.$i18n.t("pricingPersonal")],
      selectedPrice: undefined,
    };
  },

  validations: {
    editedItem: {
      session_price: {
        required: requiredIf((editedItem) => editedItem.validate),
        priceRegexp,
      },
      personal_price: {
        required: requiredIf((editedItem) => !editedItem.validate),
        priceRegexp,
      },
      prepayment: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(1),
      },
      start: {
        required,
        uniqueStartTime: helpers.withParams(
          { type: "uniqueStartTime" },
          function (value) {
            return this.uniqueStartTime(value);
          }
        ),
      },
    },
  },

  computed: {
    pricesDialogTitle() {
      return this.editedIndex === -1
        ? this.$i18n.t("pricingAdd")
        : this.$i18n.t("pricingEdit");
    },
    pricesDialogSelectLabel() {
      return this.editedIndex === -1
        ? this.$i18n.t("categoryPriceSelect")
        : this.$i18n.t("categoryPriceEdit");
    },
    priceSessionErrors() {
      const errors = [];
      if (!this.$v.editedItem.session_price.$dirty) return errors;
      !this.$v.editedItem.session_price.required &&
        errors.push(this.$i18n.t("validationSessionCost"));
      !this.$v.editedItem.session_price.priceRegexp &&
        errors.push(this.$i18n.t("validationPrice"));
      return errors;
    },
    pricePersonalErrors() {
      const errors = [];
      if (!this.$v.editedItem.personal_price.$dirty) return errors;
      !this.$v.editedItem.personal_price.required &&
        errors.push(this.$i18n.t("validationPersonalCost"));
      !this.$v.editedItem.personal_price.priceRegexp &&
        errors.push(this.$i18n.t("validationPrice"));
      return errors;
    },
    pricePrepaymentErrors() {
      const errors = [];
      if (!this.$v.editedItem.prepayment.$dirty) return errors;
      !this.$v.editedItem.prepayment.required &&
        errors.push(this.$i18n.t("validationPrepayment"));
      !this.$v.editedItem.prepayment.minValue &&
        errors.push(this.$i18n.t("validationMinValue"));
      !this.$v.editedItem.prepayment.maxValue &&
        errors.push(this.$i18n.t("validationMaxValue"));
      return errors;
    },
    priceStartErrors() {
      const errors = [];
      if (!this.$v.editedItem.start.$dirty) return errors;
      if (!this.$v.editedItem.start.required) {
        errors.push(this.$i18n.t("validationPricingTime"));
      }
      if (!this.uniqueStartTime(this.editedItem.start)) {
        errors.push(this.$i18n.t("validationPricingTimeUnique"));
      }
      return errors;
    },
  },

  filters: {
    prepaymentFilter: (value) => {
      return (value *= 100);
    },
  },

  components: {
    PrepaymentInput,
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  methods: {
    close() {
      this.dialog = false;
      this.selectedPrice = undefined;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
      this.$v.$reset();
    },
    save() {
      if (this.editedIndex > -1) {
        Object.assign(this.pricing[this.editedIndex], this.editedItem);
      } else {
        this.pricing.push(this.editedItem);
      }
      this.$emit("onChange");
      this.close();
      this.$v.$reset();
    },
    editItem(item) {
      this.editedIndex = this.pricing.indexOf(item);
      this.editedCurrentItem = Object.assign({}, item);
      this.editedItem.currency_id = this.editedCurrentItem.currency_id;
      this.editedItem.id = this.editedCurrentItem.id;
      this.editedItem.personal_price = this.editedCurrentItem.personal_price;
      this.editedItem.prepayment = this.editedCurrentItem.prepayment;
      this.editedItem.session_price = this.editedCurrentItem.session_price;
      this.editedItem.start = this.editedCurrentItem.start;

      if (this.editedItem.session_price > 0) {
        this.selectedPrice = this.items[0];
        this.editedItem.validate = true;
      }
      if (this.editedItem.personal_price > 0) {
        this.selectedPrice = this.items[1];
        this.editedItem.validate = false;
      }
      this.dialog = true;
    },
    deleteItem(item) {
      this.editedIndex = this.pricing.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      this.pricing.splice(this.editedIndex, 1);
      this.$emit("onChange");
      this.closeDelete();
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    selectionChanged() {
      this.editedItem.session_price = "";
      this.editedItem.personal_price = null;
      this.editedItem.validate = this.selectedPrice === this.items[0];
    },
    uniqueStartTime(value) {
      return !this.pricing.some((item) => item.start === value);
    },
  },
};
</script>
